import React, { useState, useEffect, useCallback } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, ChevronDownIcon, ChevronRightIcon, BoltIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import SlaPolicyModal from './SlaPolicyModal';
import ConfirmationModal from '../../components/ConfirmationModal';

interface SlaConditionClause {
  id: string;
  field: string;
  operator: string;
  value: string;
  logicalOperator: string | null;
  sortOrder: number;
}

interface SlaCondition {
  name: string;
  SlaConditionClause?: SlaConditionClause[];
}

interface SlaPolicy {
  id: string;
  name: string;
  description?: string;
  policyType: string;
  targetType: string;
  appliesTo: string;
  durationSeconds: number;
  isActive: boolean;
  priority: number;
  SlaWarningThreshold: Array<{
    id: string;
    thresholdPercentage: number;
    warningLevel: string;
  }>;
  StartCondition?: SlaCondition;
  PauseCondition?: SlaCondition;
  ResumeCondition?: SlaCondition;
  ResetCondition?: SlaCondition;
  CancelCondition?: SlaCondition;
  EndCondition?: SlaCondition;
}

interface SlaPoliciesProps {
  engagementId: string;
}

const policyTypeLabels: Record<string, string> = {
  SLA: 'SLA',
  OLA: 'OLA'
};

const targetTypeLabels: Record<string, string> = {
  RESOLUTION: 'Resolution',
  RESPONSE: 'Response',
  ONGOING: 'Ongoing'
};

const entityTypeLabels: Record<string, string> = {
  CASE: 'Case',
  INCIDENT: 'Incident',
  PROBLEM: 'Problem',
  CHANGE: 'Change',
  SERVICE_REQUEST: 'Service Request'
};

// Field labels for displaying condition clauses
const fieldLabels: Record<string, string> = {
  PRIORITY: 'Priority',
  IMPACT: 'Impact',
  SEVERITY: 'Severity',
  URGENCY: 'Urgency',
  STATE: 'State',
  ASSIGNED_TO_GROUP: 'Assigned To Group',
  ASSIGNMENT_GROUP: 'Assignment Group',
  CATEGORY: 'Category',
  CHANGE_TYPE: 'Change Type',
  CHANGE_RISK: 'Change Risk',
  CASE_TYPE: 'Case Type'
};

// Operator labels for displaying condition clauses
const operatorLabels: Record<string, string> = {
  EQUALS: '=',
  NOT_EQUALS: '≠',
  IN: 'is one of',
  NOT_IN: 'is not one of',
  CONTAINS: 'contains',
  NOT_CONTAINS: 'does not contain',
  STARTS_WITH: 'starts with',
  ENDS_WITH: 'ends with',
  GREATER_THAN: '>',
  LESS_THAN: '<',
  GREATER_THAN_OR_EQUAL: '≥',
  LESS_THAN_OR_EQUAL: '≤',
  IS_EMPTY: 'is empty',
  IS_NOT_EMPTY: 'is not empty'
};

// Convert enum values to user-friendly labels
const formatEnumValue = (value: string): string => {
  return value
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const SlaPolicies: React.FC<SlaPoliciesProps> = ({ engagementId }) => {
  const [policies, setPolicies] = useState<SlaPolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<SlaPolicy | null>(null);
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; policy: SlaPolicy | null }>({
    isOpen: false,
    policy: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [triggerModal, setTriggerModal] = useState<{ isOpen: boolean; policy: SlaPolicy | null }>({
    isOpen: false,
    policy: null,
  });
  const [expandedPolicyId, setExpandedPolicyId] = useState<string | null>(null);
  const [triggeringPolicy, setTriggeringPolicy] = useState<string | null>(null);
  const [groupsMap, setGroupsMap] = useState<Record<string, string>>({});

  const fetchPolicies = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/engagements/${engagementId}/sla-policies`);
      setPolicies(response.data || []);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching SLA policies:', err);
      setError(err.response?.data?.message || 'Failed to load SLA policies');
    } finally {
      setLoading(false);
    }
  }, [engagementId]);

  // Fetch groups for displaying group names in conditions
  const fetchGroups = useCallback(async () => {
    try {
      // Fetch all groups with a high limit to get all for the lookup map
      const response = await api.get('/groups?limit=1000');
      const groups = response.data?.groups || [];
      const map: Record<string, string> = {};
      groups.forEach((group: { id: string; name: string }) => {
        map[group.id] = group.name;
      });
      setGroupsMap(map);
    } catch (err) {
      console.error('Error fetching groups:', err);
    }
  }, []);

  useEffect(() => {
    fetchPolicies();
    fetchGroups();
  }, [fetchPolicies, fetchGroups]);

  const handleCreate = () => {
    setEditingPolicy(null);
    setShowModal(true);
  };

  const handleEdit = (policy: SlaPolicy) => {
    setEditingPolicy(policy);
    setShowModal(true);
  };

  const handleDelete = async () => {
    if (!deleteModal.policy) return;

    try {
      setIsDeleting(true);
      await api.delete(`/sla-policies/${deleteModal.policy.id}`);
      fetchPolicies();
      setDeleteModal({ isOpen: false, policy: null });
    } catch (err: any) {
      console.error('Error deleting policy:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Failed to delete policy';
      setError(errorMessage);
      setDeleteModal({ isOpen: false, policy: null });
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteModal = (policy: SlaPolicy) => {
    setDeleteModal({ isOpen: true, policy });
  };

  const closeDeleteModal = () => {
    if (!isDeleting) {
      setDeleteModal({ isOpen: false, policy: null });
    }
  };

  const openTriggerModal = (policy: SlaPolicy) => {
    setTriggerModal({ isOpen: true, policy });
  };

  const closeTriggerModal = () => {
    if (!triggeringPolicy) {
      setTriggerModal({ isOpen: false, policy: null });
    }
  };

  const handleTrigger = async () => {
    if (!triggerModal.policy) return;

    try {
      setTriggeringPolicy(triggerModal.policy.id);
      const response = await api.post(`/sla-policies/${triggerModal.policy.id}/trigger`);

      // Show success message with details
      const { processedCount, skippedCount, errorCount, totalEntities } = response.data;
      alert(
        `SLA Policy Triggered Successfully!\n\n` +
        `Total Entities: ${totalEntities}\n` +
        `Processed: ${processedCount}\n` +
        `Skipped (already has SLA): ${skippedCount}\n` +
        `Errors: ${errorCount}`
      );
      setTriggerModal({ isOpen: false, policy: null });
    } catch (err: any) {
      console.error('Error triggering policy:', err);
      alert(err.response?.data?.message || err.response?.data?.error || 'Failed to trigger policy');
    } finally {
      setTriggeringPolicy(null);
    }
  };

  const handleModalClose = (saved: boolean) => {
    setShowModal(false);
    setEditingPolicy(null);
    if (saved) {
      fetchPolicies();
    }
  };

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (secs > 0) parts.push(`${secs}s`);

    return parts.join(' ') || '0s';
  };

  const toggleExpanded = (policyId: string) => {
    setExpandedPolicyId(expandedPolicyId === policyId ? null : policyId);
  };

  // Helper to format a single value based on field type
  const formatSingleValue = (fieldName: string, val: string): string => {
    // Check if the field is an assignment group field and value looks like a UUID
    if ((fieldName === 'ASSIGNED_TO_GROUP' || fieldName === 'ASSIGNMENT_GROUP') &&
        val && val.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
      return groupsMap[val] || val;
    }
    // Format enum values (STATE, PRIORITY, etc.)
    return formatEnumValue(val);
  };

  const formatClauseValue = (clause: SlaConditionClause): string => {
    const { field, operator, value } = clause;

    // For IS_EMPTY and IS_NOT_EMPTY, no value is needed
    if (operator === 'IS_EMPTY' || operator === 'IS_NOT_EMPTY') {
      return '';
    }

    // For IN and NOT_IN operators, parse the JSON array
    if (operator === 'IN' || operator === 'NOT_IN') {
      try {
        const values = JSON.parse(value);
        return values.map((v: string) => formatSingleValue(field, v)).join(', ');
      } catch {
        return value;
      }
    }

    // For other operators, format the single value
    return formatSingleValue(field, value);
  };

  const renderClause = (clause: SlaConditionClause, _appliesTo: string): string => {
    const field = fieldLabels[clause.field] || clause.field;
    const operator = operatorLabels[clause.operator] || clause.operator;
    const value = formatClauseValue(clause);

    if (clause.operator === 'IS_EMPTY' || clause.operator === 'IS_NOT_EMPTY') {
      return `${field} ${operator}`;
    }

    if (clause.operator === 'EQUALS') {
      return `${field} = "${value}"`;
    }

    return `${field} ${operator} ${value}`;
  };

  const renderConditions = (policy: SlaPolicy) => {
    const conditions = [
      { label: 'Start', condition: policy.StartCondition },
      { label: 'Pause', condition: policy.PauseCondition },
      { label: 'Resume', condition: policy.ResumeCondition },
      { label: 'Reset', condition: policy.ResetCondition },
      { label: 'Cancel', condition: policy.CancelCondition },
      { label: 'End', condition: policy.EndCondition }
    ];

    const activeConditions = conditions.filter(c => c.condition?.name);

    if (activeConditions.length === 0) {
      return (
        <div className="text-sm text-gray-500 italic">
          No conditions configured for this policy
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {activeConditions.map(({ label, condition }) => (
          <div key={label} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
              {label} Condition
            </div>
            <div className="text-sm font-medium text-gray-900 mb-2">
              {condition?.name}
            </div>
            {condition?.SlaConditionClause && condition.SlaConditionClause.length > 0 && (
              <div className="text-xs text-gray-600 space-y-1">
                {condition.SlaConditionClause.map((clause, index) => (
                  <div key={clause.id} className="flex items-start">
                    {index > 0 && clause.logicalOperator && (
                      <span className="font-semibold mr-1">{clause.logicalOperator}</span>
                    )}
                    <span className="italic">
                      Clause applicable to {entityTypeLabels[policy.appliesTo] || policy.appliesTo} where {renderClause(clause, policy.appliesTo)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-lg text-gray-600">Loading SLA policies...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-gray-900">SLA Policies</h3>
          <p className="text-sm text-gray-500 mt-1">
            Define SLA policies for this engagement
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Policy
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {policies.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500 mb-4">No SLA policies configured</p>
          <button
            onClick={handleCreate}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Create First Policy
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                  {/* Expand/Collapse */}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name & Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Target
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Applies To
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Warnings
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {policies.map((policy) => (
                <React.Fragment key={policy.id}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-3 py-4">
                      <button
                        onClick={() => toggleExpanded(policy.id)}
                        className="text-gray-500 hover:text-gray-700 focus:outline-none"
                      >
                        {expandedPolicyId === policy.id ? (
                          <ChevronDownIcon className="h-5 w-5" />
                        ) : (
                          <ChevronRightIcon className="h-5 w-5" />
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      {policy.name}
                    </div>
                    {policy.description && (
                      <div className="text-sm text-gray-500 mt-1">
                        {policy.description}
                      </div>
                    )}
                    <div className="text-xs text-gray-400 mt-1">
                      Priority: {policy.priority}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800">
                      {policyTypeLabels[policy.policyType] || policy.policyType}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {targetTypeLabels[policy.targetType] || policy.targetType}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                      {entityTypeLabels[policy.appliesTo] || policy.appliesTo}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDuration(policy.durationSeconds)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {policy.SlaWarningThreshold.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {policy.SlaWarningThreshold.map((threshold) => (
                          <span
                            key={threshold.id}
                            className="inline-flex px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800"
                          >
                            {threshold.thresholdPercentage}%
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400">None</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {policy.isActive ? (
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-3">
                      <button
                        onClick={() => openTriggerModal(policy)}
                        disabled={!policy.isActive || triggeringPolicy === policy.id}
                        className={`p-1 rounded transition-colors ${
                          policy.isActive && triggeringPolicy !== policy.id
                            ? 'text-green-600 hover:text-green-900 hover:bg-green-50'
                            : 'text-gray-400 cursor-not-allowed'
                        }`}
                        title="Manually trigger SLA for existing entities"
                      >
                        {triggeringPolicy === policy.id ? (
                          <span className="text-xs">Triggering...</span>
                        ) : (
                          <BoltIcon className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleEdit(policy)}
                        className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded transition-colors"
                        title="Edit policy"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => openDeleteModal(policy)}
                        className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded transition-colors"
                        title="Delete policy"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedPolicyId === policy.id && (
                  <tr>
                    <td colSpan={9} className="px-6 py-4 bg-gray-50">
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 mb-2">
                            SLA Conditions
                          </h4>
                          {renderConditions(policy)}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Policy Details */}
      {policies.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-blue-900 mb-2">Policy Matching Order</h4>
          <p className="text-sm text-blue-800">
            Policies are evaluated in priority order (lowest number = highest priority).
            The first matching policy will be applied to each ticket.
          </p>
        </div>
      )}

      {showModal && (
        <SlaPolicyModal
          engagementId={engagementId}
          policy={editingPolicy}
          onClose={handleModalClose}
        />
      )}

      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        title="Delete SLA Policy"
        message={`Are you sure you want to delete the policy "${deleteModal.policy?.name}"? This action cannot be undone and will affect all associated SLA instances.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmButtonClass="bg-red-600 hover:bg-red-700 text-white"
        isLoading={isDeleting}
      />

      <ConfirmationModal
        isOpen={triggerModal.isOpen}
        onClose={closeTriggerModal}
        onConfirm={handleTrigger}
        title="Trigger SLA Policy"
        message={`Manually trigger SLA policy "${triggerModal.policy?.name}"? This will apply the policy to all matching existing entities that don't already have an SLA instance for this target type.`}
        confirmLabel="Trigger"
        cancelLabel="Cancel"
        confirmButtonClass="bg-green-600 hover:bg-green-700 text-white"
        isLoading={!!triggeringPolicy}
      />
    </div>
  );
};

export default SlaPolicies;
